import { AudioType } from '@modules/feedback/components/AudioType';
import { getAllHighlights } from '@modules/feedback/components/FeedbackDetails/utils';
import { ReviewType } from '@modules/feedback/components/ReviewType';
import { Highlight } from '@modules/feedback/types';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import {
  Feedback,
  FeedbackContent,
  FeedbackContentType,
  FeedbackDetailsConversationVariant,
  FeedbackLabel,
} from 'v2/core/entities/feedback';
import ErrorBoundary from 'v2/views/components/error/boundary';
import { ConversationType } from '../../ConversationType';
import { ForumMessageType } from '../../ForumMessageType';
import { SurveyType } from '../../SurveyType';

interface FeedbackDetailsConversationProps {
  feedback: Feedback;
  selectedLabels?: FeedbackLabel[];
  showOriginalLanguage?: boolean;
  variant?: FeedbackDetailsConversationVariant;
}

export const FeedbackDetailsConversation = ({
  feedback,
  selectedLabels,
  showOriginalLanguage = false,
  variant = 'default',
}: FeedbackDetailsConversationProps) => {
  const [conversationType, setConversationType] =
    useState<FeedbackContent['type']>();

  useEffect(() => {
    setConversationType(feedback.content.type);
  }, [feedback.content.type]);

  const highlights: { [key: string]: Highlight[] } = selectedLabels?.length
    ? getAllHighlights(selectedLabels)
    : {};

  const mapCoversationTypeToComponent = () => {
    switch (conversationType) {
      case FeedbackContentType.Conversation:
        return (
          <ConversationType
            feedback={feedback}
            highlights={highlights}
            showOriginalLanguage={showOriginalLanguage}
          />
        );

      case FeedbackContentType.Review:
        return (
          <ReviewType
            feedback={feedback}
            highlights={highlights}
            showOriginalLanguage={showOriginalLanguage}
          />
        );

      case FeedbackContentType.Survey:
        return (
          <SurveyType
            feedback={feedback}
            highlights={highlights}
            showOriginalLanguage={showOriginalLanguage}
          />
        );

      case FeedbackContentType.Forum:
        return (
          <ForumMessageType
            feedback={feedback}
            highlights={highlights}
            showOriginalLanguage={showOriginalLanguage}
            variant={variant}
          />
        );

      case FeedbackContentType.Audio:
        return (
          <AudioType
            feedback={feedback}
            predictionHighlights={highlights}
            showOriginalLanguage={showOriginalLanguage}
            selectedLabels={selectedLabels}
          />
        );

      default:
        return null;
    }
  };

  return (
    <ErrorBoundary>
      <Box height="100%">{mapCoversationTypeToComponent()}</Box>
    </ErrorBoundary>
  );
};
